import { useEffect, useState } from "react";
import { Menu, User, Settings, Lock, Wrench, Sparkles, AlertTriangle, CalendarClock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CreatorLockModal from "@/components/creator-lock/CreatorLockModal";
import DemoModeModal from "@/components/demo-mode/DemoModeModal";
import AgentSchedulesModal from "@/components/agent-schedules/AgentSchedulesModal";
import ReadOnlyModeBadge from "@/components/layout/ReadOnlyModeBadge";
import { useIsAdmin } from "@/hooks/useIsAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hook `useIsAdmin` przeniesiony do `@/hooks/useIsAdmin` (2026-02, round 12)
// — reuse w Sidebar dla widoczności item „Administracja".

/**
 * Sprawdzenie „czy zalogowany user jest adminem" — moved to
 * `@/hooks/useIsAdmin`. Zostawiamy istniejące importy dla API.
 */

const TopHeader = ({ onMenuClick }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const [lockOpen, setLockOpen] = useState(false);
  const [lockEnabled, setLockEnabled] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [schedulesOpen, setSchedulesOpen] = useState(false);

  const demoMode = !!user?.demo_mode;

  // Fetch stanu Creator Lock — dla badge. Admin widzi realny stan; non-admin
  // (technicznie nie dojdzie tutaj, bo middleware go zablokuje) — false.
  useEffect(() => {
    if (isAdmin !== true) return;
    let cancelled = false;
    const fetchStatus = () => {
      axios
        .get(`${API}/creator-lock/status`, { withCredentials: true })
        .then((r) => !cancelled && setLockEnabled(!!r.data?.enabled))
        .catch(() => {});
    };
    fetchStatus();
    // Odśwież co 30s żeby badge był aktualny gdy zmieniono w innej karcie.
    const t = setInterval(fetchStatus, 30000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [isAdmin]);

  const handleLockSaved = (data) => {
    setLockEnabled(!!data?.enabled);
  };

  return (
    <header className="top-header">
      <button
        onClick={onMenuClick}
        className="md:hidden p-2 hover:bg-gray-100 rounded-md"
        data-testid="menu-toggle"
      >
        <Menu className="w-5 h-5" />
      </button>

      <div className="flex items-center gap-3 ml-auto">
        {/* Read-only badge — dla user.role === "viewer" (2026-02-18). */}
        <ReadOnlyModeBadge />

        {/* Badge PROD/DEMO — zawsze widoczny (żeby user wiedział gdzie jest). */}
        <div
          className={`hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[10px] uppercase tracking-widest font-semibold ${
            demoMode
              ? "bg-amber-50 border-amber-300 text-amber-800"
              : "bg-emerald-50 border-emerald-300 text-emerald-800"
          }`}
          data-testid="mode-badge"
        >
          {demoMode ? (
            <>
              <AlertTriangle className="w-3 h-3" />
              Demo
            </>
          ) : (
            <>Prod</>
          )}
        </div>

        {/* Badge Closed Beta — widoczny tylko dla admina i tylko gdy lock ON.
            Non-admin nigdy tu nie dojdzie (middleware ich odetnie), więc UI-side
            gate wystarcza dla admin-only visibility. */}
        {isAdmin && lockEnabled && (
          <div
            className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-[10px] uppercase tracking-widest font-semibold"
            data-testid="closed-beta-badge"
          >
            <Lock className="w-3 h-3" />
            Closed Beta
          </div>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-3 hover:bg-gray-100 rounded-md p-2"
              data-testid="user-menu"
            >
              <Avatar className="w-8 h-8">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-[#0F1F3D] text-white text-xs">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-gray-700">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Moje konto</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => navigate("/profil")}
              data-testid="user-menu-profile"
              className="cursor-pointer"
            >
              <User className="w-4 h-4 mr-2" />
              Profil
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setDemoOpen(true)}
              data-testid="user-menu-demo-mode"
              className="cursor-pointer"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Tryb:{" "}
              <span className={`ml-1 font-semibold ${demoMode ? "text-amber-700" : "text-emerald-700"}`}>
                {demoMode ? "Demo" : "Production"}
              </span>
            </DropdownMenuItem>

            {isAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setLockOpen(true)}
                  data-testid="user-menu-creator-lock"
                  className="cursor-pointer"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Creator Lock
                  {lockEnabled && (
                    <span className="ml-auto text-[10px] font-semibold text-amber-600 uppercase tracking-wide">
                      ON
                    </span>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate("/dev-tools")}
                  data-testid="user-menu-dev-tools"
                  className="cursor-pointer"
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  Dev Tools
                </DropdownMenuItem>
              </>
            )}

            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setSchedulesOpen(true)}
              data-testid="user-menu-agent-schedules"
              className="cursor-pointer"
            >
              <CalendarClock className="w-4 h-4 mr-2" />
              Harmonogramy agentów
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="user-menu-settings" disabled>
              <Settings className="w-4 h-4 mr-2" />
              Ustawienia
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <CreatorLockModal
        open={lockOpen}
        onOpenChange={setLockOpen}
        onSaved={handleLockSaved}
      />
      <DemoModeModal open={demoOpen} onOpenChange={setDemoOpen} />
      <AgentSchedulesModal open={schedulesOpen} onOpenChange={setSchedulesOpen} />
    </header>
  );
};

export default TopHeader;
